import React, { useEffect, useState } from "react";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  setAddItemToCart,
  setIncreaseItemQTY,
  setDecreaseItemQTY,
  setRemoveItemFromCart,
} from "../../../app/CartSlice";
import {
  PlusOutlined,
  MinusOutlined,
  ShoppingCartOutlined,
  DeleteOutlined,
} from "@ant-design/icons";

const CardComponent = ({ product, productInfo, currCart, handleCartPopUpChange, showThreeCards }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [selectedSize, setSelectedSize] = useState(null);
  const [currIdx, setCurrIdx] = useState(0);
  const [selectedProductPrice, setSelectedProductPrice] = useState(0);
  const [selectedProductMrp, setSelectedProductMrp] = useState(0);
  const [selectedDiscount, setSelectedDiscount] = useState(0);
  const [currProduct, setCurrProduct] = useState([]);

  // Subscribe to Redux cart items
  const cartItems = useSelector((state) => state.cart?.cartItems) || [];

  useEffect(() => {
    getProductDetailInfo();
  }, [product]);

  const getProductDetailInfo = async () => {
    if (productInfo) {
      const response = await productInfo();
      setCurrProduct(response);
    }
  };

  useEffect(() => {
    if (product && product.productPackageDetails && product.productPackageDetails.length > 0) {
      const defaultSize = product.productPackageDetails[0];
      handleSize(defaultSize, 0);

      const selectedProduct = currProduct?.find(
        (item) => item.packageCode === defaultSize.packageSize
      );

      if (selectedProduct) {
        setSelectedProductMrp(selectedProduct.unitPrice);
        setSelectedProductPrice(selectedProduct.finalPrice);
        setSelectedDiscount(selectedProduct.discountPercentage || 0);
      }
    }
  }, [product, currProduct]);

  const handleSize = (size, idx) => {
    setSelectedSize(size);
    setCurrIdx(idx);
    const sizeName = typeof size === "object" ? size.packageSize : size;
    const selected = currProduct?.find((item) => item.packageCode === sizeName);
    if (selected) {
      setSelectedProductMrp(selected.unitPrice);
      setSelectedProductPrice(selected.finalPrice);
      setSelectedDiscount(selected.discountPercentage || 0);
    }
  };

  // Extract current size key
  const currentSizeKey =
    typeof selectedSize === "object" && selectedSize?.packageSize
      ? String(selectedSize.packageSize).trim().toLowerCase()
      : String(selectedSize || "standard").trim().toLowerCase();

  // Find if this product with the currently selected size is in the cart
  const cartItem = cartItems.find((item) => {
    const itemSizeKey =
      typeof item.selectedSize === "object" && item.selectedSize?.packageSize
        ? String(item.selectedSize.packageSize).trim().toLowerCase()
        : String(item.selectedSize || "standard").trim().toLowerCase();
    return item.id === product?.id && itemSizeKey === currentSizeKey;
  });

  const cartQuantity = cartItem ? Number(cartItem.cartQuantity) || 0 : 0;

  const handleAddToCart = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();

    let priceToUse = selectedProductPrice;
    if (!priceToUse && currProduct && currProduct.length > 0) {
      const sizeName = typeof selectedSize === "object" ? selectedSize?.packageSize : selectedSize;
      const selected = currProduct.find((item) => item.packageCode === sizeName);
      if (selected) {
        priceToUse = selected.finalPrice || selected.unitPrice;
      } else if (currProduct[0]) {
        priceToUse = currProduct[0].finalPrice || currProduct[0].unitPrice;
      }
    }
    const numericPrice = Number(priceToUse || selectedProductMrp || 0);
    const sizeToUse =
      selectedSize ||
      (product?.productPackageDetails && product.productPackageDetails[0]) || {
        packageSize: "Standard",
      };

    const itemTobeAdded = {
      ...product,
      selectedProductPrice: numericPrice,
      price: numericPrice,
      selectedSize: sizeToUse,
    };

    dispatch(setAddItemToCart(itemTobeAdded));
  };

  const handleIncrement = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (cartItem) {
      dispatch(setIncreaseItemQTY(cartItem.cartItemId || cartItem.id));
    }
  };

  const handleDecrement = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (cartItem) {
      if (cartQuantity > 1) {
        dispatch(setDecreaseItemQTY(cartItem.cartItemId || cartItem.id));
      } else {
        dispatch(setRemoveItemFromCart(cartItem));
      }
    }
  };

  const handleClick = () => {
    navigate(`/productdetail/${product.id}`);
    window.scrollTo(0, 0);
  };

  const imageSrc = product?.imageName
    ? product.imageName.startsWith("http") || product.imageName.startsWith("data:")
      ? product.imageName
      : `${process.env.REACT_APP_IMAGE_URL || ""}${product.imageName}`
    : product?.imageUrl || "";

  return (
    <div className="w-full max-w-[320px] mx-auto bg-white rounded-2xl border border-amber-100/80 shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:shadow-[0_8px_30px_rgba(161,122,23,0.15)] hover:border-amber-300 transition-all duration-300 relative flex flex-col justify-between overflow-hidden group">
      {/* Discount Badge */}
      {selectedDiscount > 0 && (
        <div className="absolute top-3 left-3 bg-gradient-to-r from-amber-600 to-amber-500 text-white font-bold text-xs py-1 px-3.5 rounded-full shadow-md z-10 tracking-wide">
          {selectedDiscount}% OFF
        </div>
      )}

      {/* Image Container */}
      <div
        className="w-full h-64 flex items-center justify-center cursor-pointer overflow-hidden relative bg-amber-50/20"
        onClick={handleClick}
      >
        <img
          alt={product?.name || "Product Image"}
          src={imageSrc}
          className="w-full h-full object-cover zoom-image"
        />
      </div>

      {/* Product Information */}
      <div className="p-4 flex-1 flex flex-col justify-between">
        <div>
          <h3
            className="font-sans font-semibold text-gray-800 text-base line-clamp-2 hover:text-[#A17A17] transition-colors cursor-pointer min-h-[2.75rem] leading-snug"
            onClick={handleClick}
            title={product?.name}
          >
            {product?.name}
          </h3>

          {/* Size Pills */}
          <div className="flex flex-wrap gap-1.5 my-1">
            {product?.productPackageDetails?.map((size, idx) => {
              const isSelected =
                (selectedSize === size || (selectedSize?.packageSize && selectedSize.packageSize === size.packageSize)) &&
                currIdx === idx;
              return (
                <button
                  key={idx}
                  type="button"
                  className={`px-3 py-1 text-xs rounded-full font-medium transition-all duration-200 cursor-pointer ${
                    isSelected
                      ? "bg-[#A17A17] text-white shadow-sm border border-[#A17A17]"
                      : "bg-[#FFF0C0]/50 text-gray-700 hover:bg-[#FFF0C0] border border-amber-200/60"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSize(size, idx);
                  }}
                >
                  {size.packageSize}
                </button>
              );
            })}
          </div>
        </div>
        <div className="">
        {/* Pricing Section */}
        <div className="flex justify-end items-baseline gap-2 mb-2">
          {selectedProductMrp > selectedProductPrice && (
            <span className="text-xs text-gray-400 line-through font-medium">
              INR {selectedProductMrp}
            </span>
          )}
          <span className="text-lg font-bold text-[#A17A17]">
            INR {selectedProductPrice || selectedProductMrp || 0}
          </span>
        </div>

        {/* Add to Cart / Quantity Counter Controller */}
        <div className="w-full">
          {cartQuantity === 0 ? (
            <button
              type="button"
              onClick={handleAddToCart}
              className="w-full h-11 bg-gradient-to-r from-[#A17A17] to-[#B88E28] hover:from-[#8C6913] hover:to-[#A17A17] text-white font-medium text-sm rounded-xl shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 active:scale-98 cursor-pointer"
            >
              <ShoppingCartOutlined className="text-base" />
              <span>Add To Cart</span>
            </button>
          ) : (
            <div className="w-full h-11 bg-[#FFF8E7] border border-[#A17A17] rounded-xl flex items-center justify-between px-2 shadow-inner">
              <button
                type="button"
                onClick={handleDecrement}
                className="w-8 h-8 rounded-lg bg-[#A17A17] hover:bg-[#8C6913] text-white flex items-center justify-center transition-all duration-150 active:scale-90 shadow-sm cursor-pointer"
                title={cartQuantity === 1 ? "Remove from cart" : "Decrease quantity"}
              >
                {cartQuantity === 1 ? (
                  <DeleteOutlined className="text-xs" />
                ) : (
                  <MinusOutlined className="text-xs" />
                )}
              </button>

              <div className="flex flex-col items-center justify-center select-none px-2">
                <span className="text-base font-bold text-[#4C3000] leading-none">
                  {cartQuantity}
                </span>
                <span className="text-[10px] text-amber-900/80 font-medium leading-none mt-0.5">
                  in cart
                </span>
              </div>

              <button
                type="button"
                onClick={handleIncrement}
                className="w-8 h-8 rounded-lg bg-[#A17A17] hover:bg-[#8C6913] text-white flex items-center justify-center transition-all duration-150 active:scale-90 shadow-sm cursor-pointer"
                title="Increase quantity"
              >
                <PlusOutlined className="text-xs" />
              </button>
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default CardComponent;
